import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react'

import ThemeHeader from './components/ThemeHeader'
import ThemeFooter from './components/ThemeFooter'
import Header from './components/Header'
import ClientLogin from './components/ClientLogin'
import LoginModal from './components/LoginModal'
import ComingSoonModal from './components/ComingSoonModal'
import UpdateModal from './components/UpdateModal'
import InstructionModal from './components/InstructionModal'
import PasswordChangedModal from './components/PasswordChangedModal'
import NewModelSelector from './components/NewModelSelector'
import ConfiguratorOptions from './components/ConfiguratorOptions'
import PreviewPanel from './components/PreviewPanel'
import FloatingPrice from './components/FloatingPrice'
import LiftNotificationModal from './components/LiftNotificationModal'
import ValidationMessage from './components/ValidationMessage'

// Lazy load heavy components
const ClientPanel = lazy(() => import('./components/ClientPanel'))
const AdminPanel = lazy(() => import('./components/AdminPanel'))
const SummaryModal = lazy(() => import('./components/SummaryModal'))
// Removed Google Drive uploader and FirebaseService as per request
import { useConfiguration } from './hooks/useConfiguration'
import { auth } from './config/firebase'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import ProfilesService from './services/ProfilesService'
import LoginHistoryService from './services/LoginHistoryService'
import { setUserSession, getUserSession, clearUserSession, setAdminSession, getAdminSession, clearAdminSession } from './utils/sessionStorage'
import { useModelData } from './hooks/useModelData'
import { usePriceCalculation } from './hooks/usePriceCalculation'
import { useConfigurationValidation } from './hooks/useConfigurationValidation'
import { preloadCommonImages, preloadCategoryImages } from './hooks/useImagePreloader'
import { imagePreloader } from './utils/imagePreloader'
import { isLiftAvailable, isStandardAvailable } from './utils/optionGenerator'
import { findModelData } from './utils/directProductCode'
import type { ModelData } from './types'
import './styles/components.css'

function App() {
  const { currentModel, config, updateConfig, setCurrentModel, checkAndHandleLiftSelection, showLiftModal, closeLiftModal, resetConfig, resetConfigExceptVariant, loadConfigFromCode } = useConfiguration()
  const { modelsData, modelGroups } = useModelData()
  const validation = useConfigurationValidation(currentModel, config)
  const pricing = usePriceCalculation(currentModel, config)
  const [showSummary, setShowSummary] = useState(false)
  const [showValidationMessage, setShowValidationMessage] = useState(false)
  const [previousVariant, setPreviousVariant] = useState<string>('')
  
  // VAT mode state - shared between FloatingPrice and ConfiguratorOptions
  type PriceMode = 'netto' | 'vat8' | 'vat23'
  const [priceMode, setPriceMode] = useState<PriceMode>('netto')
  
  // Removed client details and configuration documents state
  
  // Debug
  console.log('Available model groups:', modelGroups)
  console.log('Available modelsData keys:', Object.keys(modelsData))
  
  // Model selection state
  const [selectedGroup, setSelectedGroup] = useState<string>('')
  const [selectedModel, setSelectedModel] = useState<string>('')
  
  // Admin panel state
  const [showAdminPanel, setShowAdminPanel] = useState(false)
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState<boolean>(false)

  // Client panel state
  const [showClientLogin, setShowClientLogin] = useState(false)
  const [showClientPanel, setShowClientPanel] = useState(false)
  const [isClientLoggedIn, setIsClientLoggedIn] = useState<boolean>(false)
  const [currentClient, setCurrentClient] = useState<{ id: string; username: string; email: string } | null>(null)
  const [showComingSoon, setShowComingSoon] = useState(false)
  
  // Update modal state
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  
  // Instruction modal state
  const [showInstructionModal, setShowInstructionModal] = useState(false)
  
  // Product code modal state
  const [showProductCodeModal, setShowProductCodeModal] = useState(false)
  
  // Password changed modal state
  const [showPasswordChangedModal, setShowPasswordChangedModal] = useState(false)
  
  // Site authentication state - simple token check in storage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const token = localStorage.getItem('configurator_auth') || sessionStorage.getItem('configurator_auth')
      return !!token
    } catch {
      return false
    }
  })
  const [isAppEntranceVisible, setIsAppEntranceVisible] = useState(false)
  
  // Track if client was restored from session to prevent double login recording
  const [clientRestoredFromSession, setClientRestoredFromSession] = useState(false)
  
  // Flags to prevent race conditions during role switching
  const [isRoleSwitching, setIsRoleSwitching] = useState(false)
  
  // Debounce login recording to prevent multiple calls
  // Używamy useRef żeby uniknąć re-renders podczas timer management
  const loginDebounceTimers = useRef<{ [userId: string]: NodeJS.Timeout }>({})
  
  const recordLoginDebounced = useCallback((userId: string) => {
    // Clear existing timer if any
    if (loginDebounceTimers.current[userId]) {
      clearTimeout(loginDebounceTimers.current[userId])
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      LoginHistoryService.recordLogin(userId)
      // Clean up timer from ref (no state update needed)
      delete loginDebounceTimers.current[userId]
    }, 500) // 500ms delay
    
    // Store timer in ref (no state update = no re-render)
    loginDebounceTimers.current[userId] = timer
  }, [])
  
  // Preload all perk images on app start
  useEffect(() => {
    imagePreloader.preloadAllImages().catch(console.warn)
  }, [])

  useEffect(() => {
    if (!isAuthenticated || showPasswordChangedModal) {
      setIsAppEntranceVisible(false)
      return
    }

    setIsAppEntranceVisible(false)

    const timer = window.setTimeout(() => {
      setIsAppEntranceVisible(true)
    }, 110)

    return () => {
      window.clearTimeout(timer)
    }
  }, [isAuthenticated, showPasswordChangedModal])
  
  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      // Clear all login debounce timers
      Object.values(loginDebounceTimers.current).forEach(timer => {
        clearTimeout(timer)
      })
      loginDebounceTimers.current = {}
      
      // Clear signOut timeout if exists
      if (signOutTimeout.current) {
        clearTimeout(signOutTimeout.current)
      }
    }
  }, [])

  // Check if password has been changed (updated timestamp) and logout if needed
  useEffect(() => {
    let lastCheckTime = 0
    const CHECK_THROTTLE = 5000 // Sprawdzaj max co 5 sekund, nawet jeśli user klika częściej
    
    const checkPasswordChanged = async () => {
      // Throttle - nie sprawdzaj częściej niż co 5 sekund
      const now = Date.now()
      if (now - lastCheckTime < CHECK_THROTTLE) {
        console.log('⏱️ Throttled - ostatnie sprawdzenie było', Math.round((now - lastCheckTime) / 1000), 'sekund temu')
        return
      }
      lastCheckTime = now
      
      console.log('🔍 === SPRAWDZAM HASŁO ===')
      
      try {
        // Check both localStorage and sessionStorage
        const authToken = localStorage.getItem('configurator_auth') || sessionStorage.getItem('configurator_auth')
        console.log('🔑 Auth token istnieje:', !!authToken)
        
        if (!authToken) {
          console.log('❌ Brak tokenu - użytkownik NIE jest zalogowany')
          return // User not logged in
        }

        console.log('✅ Użytkownik JEST zalogowany - pobieram JSON...')

        // Fetch current config with aggressive cache-busting
        const timestamp = new Date().getTime()
        const random = Math.random()
        const url = `/xghfsjpsd.json?t=${timestamp}&r=${random}`
        console.log('📡 Fetch URL:', url)
        
        const response = await fetch(url, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
        
        console.log('📥 Response status:', response.status)
        
        if (!response.ok) {
          console.error('❌ Błąd pobierania JSON')
          return
        }
        
        const config = await response.json()
        const newUpdated = config.updated
        console.log('🆕 Timestamp z JSON:', newUpdated)
        
        // Get stored timestamp from both storage locations
        const storedUpdatedLocal = localStorage.getItem('configurator_auth_updated')
        const storedUpdatedSession = sessionStorage.getItem('configurator_auth_updated')
        const storedUpdated = storedUpdatedLocal || storedUpdatedSession
        console.log('💾 Timestamp z localStorage:', storedUpdatedLocal)
        console.log('💾 Timestamp z sessionStorage:', storedUpdatedSession)
        console.log('💾 Używany timestamp:', storedUpdated)
        
        // If no timestamp stored, save current one (backward compatibility for already logged users)
        if (!storedUpdated) {
          console.log('⚠️ BRAK zapisanego timestampu - zapisuję aktualny:', newUpdated)
          if (localStorage.getItem('configurator_auth')) {
            localStorage.setItem('configurator_auth_updated', newUpdated)
            console.log('✅ Zapisano do localStorage')
          } else if (sessionStorage.getItem('configurator_auth')) {
            sessionStorage.setItem('configurator_auth_updated', newUpdated)
            console.log('✅ Zapisano do sessionStorage')
          }
          return // Don't check on first save
        }
        
        console.log('🔄 Porównuję timestampy:')
        console.log('   Zapisany:', storedUpdated)
        console.log('   Z JSON:', newUpdated)
        console.log('   Czy różne?', storedUpdated !== newUpdated)
        
        if (storedUpdated !== newUpdated) {
          console.error('🚨🚨🚨 HASŁO ZOSTAŁO ZMIENIONE! 🚨🚨🚨')
          console.error('Stary timestamp:', storedUpdated)
          console.error('Nowy timestamp:', newUpdated)
          
          // Clear all auth data from both storage locations
          localStorage.removeItem('configurator_auth')
          localStorage.removeItem('configurator_auth_updated')
          sessionStorage.removeItem('configurator_auth')
          sessionStorage.removeItem('configurator_auth_updated')
          
          console.error('🗑️ Wyczyszczono storage')
          
          // DON'T set isAuthenticated to false yet - let modal show first
          // setIsAuthenticated will be set to false when modal is confirmed
          
          console.error('🔄 Pokazuję modal o zmianie hasła')
          
          // Show modal instead of alert
          setShowPasswordChangedModal(true)
        } else {
          console.log('✅ Timestampy identyczne - hasło NIE zmieniło się')
        }
      } catch (error) {
        console.error('❌ Błąd podczas sprawdzania hasła:', error)
      }
      
      console.log('🔍 === KONIEC SPRAWDZANIA ===')
    }
    
    // Check on mount
    console.log('🚀 Montowanie komponentu - pierwsze sprawdzenie hasła')
    checkPasswordChanged()
    
    // Check on ANY user interaction (click, keypress, scroll)
    const handleInteraction = (event: Event) => {
      console.log('👆 Interakcja użytkownika:', event.type)
      checkPasswordChanged()
    }
    
    // Dodaj event listenery - używamy capture phase dla pewności
    document.addEventListener('click', handleInteraction, true)
    document.addEventListener('keydown', handleInteraction, true)
    document.addEventListener('scroll', handleInteraction, true)
    
    console.log('👂 Event listenery zainstalowane (click, keydown, scroll)')
    
    // Cleanup
    return () => {
      console.log('🧹 Usuwanie event listenerów')
      document.removeEventListener('click', handleInteraction, true)
      document.removeEventListener('keydown', handleInteraction, true)
      document.removeEventListener('scroll', handleInteraction, true)
    }
  }, []) // Empty dependency array - set up listeners once

  // Check for version updates
  useEffect(() => {
    const checkVersion = async () => {
      try {
        // Check if we should show update modal after reload
        const shouldShowUpdate = localStorage.getItem('show_update_modal')
        if (shouldShowUpdate === 'true') {
          localStorage.removeItem('show_update_modal')
          setShowUpdateModal(true)
          return
        }

        const response = await fetch('/version.json?t=' + Date.now(), {
          cache: 'no-store'
        })
        if (!response.ok) {
          console.log('Version file not found')
          return
        }
        
        const newVersion = await response.json()
        const storedVersion = localStorage.getItem('app_version')
        
        if (storedVersion && storedVersion !== newVersion.timestamp.toString()) {
          console.log('🔄 New version detected!')
          
          // Preserve auth tokens
          const authToken = localStorage.getItem('configurator_auth')
          const adminSession = localStorage.getItem('admin_session')
          const userSession = localStorage.getItem('user_session')
          
          // Clear all storage
          localStorage.clear()
          sessionStorage.clear()
          
          // Restore auth tokens
          if (authToken) localStorage.setItem('configurator_auth', authToken)
          if (adminSession) localStorage.setItem('admin_session', adminSession)
          if (userSession) localStorage.setItem('user_session', userSession)
          
          // Store new version and flag to show modal
          localStorage.setItem('app_version', newVersion.timestamp.toString())
          localStorage.setItem('show_update_modal', 'true')
          
          // Reload page
          window.location.reload()
        } else if (!storedVersion) {
          // First visit - store version
          localStorage.setItem('app_version', newVersion.timestamp.toString())
        }
      } catch (error) {
        console.warn('Version check failed:', error)
      }
    }
    
    checkVersion()
  }, [])

  // Set background image
  useEffect(() => {
    document.body.style.backgroundImage = "url('/images/wizkabackground-compressed.jpg')"
    document.body.style.backgroundSize = 'cover'
    document.body.style.backgroundPosition = 'center center'
    document.body.style.backgroundAttachment = 'fixed'
    document.body.style.backgroundRepeat = 'no-repeat'
    
    return () => {
      document.body.style.backgroundImage = ''
    }
  }, [])

  // Listen for instruction modal events from Header
  useEffect(() => {
    const handleShowInstruction = () => {
      setShowInstructionModal(true)
    }
    
    window.addEventListener('showInstructionModal', handleShowInstruction)
    return () => {
      window.removeEventListener('showInstructionModal', handleShowInstruction)
    }
  }, [])

  // Restore admin login from localStorage only if remember me was checked
  useEffect(() => {
    const adminSession = getAdminSession()
    if (adminSession.isLoggedIn && adminSession.rememberMe) {
      setIsAdminLoggedIn(true)
      console.log('🔄 Admin session restored from localStorage')
    }
  }, [])

  // Restore client session from storage if remember me was checked
  useEffect(() => {
    const clientSession = getUserSession()
    if (clientSession && clientSession.rememberMe) {
      setIsClientLoggedIn(true)
      setCurrentClient(clientSession.user)
      setClientRestoredFromSession(true)
      console.log('🔄 Client session restored from localStorage:', clientSession.user.username)
      
      // Record session restoration as login (debounced)
      recordLoginDebounced(clientSession.user.id)
    }
  }, [])

  // Cache profili żeby uniknąć duplikatów zapytań
  const profileCache = useRef<{ [uid: string]: any }>({})
  const lastAuthCheck = useRef<string>('')

  // Skonsolidowany listener Firebase Auth - obsługuje zarówno klientów jak i adminów
  // Dodane refy żeby uniknąć pętli podczas signOut
  const isSigningOut = useRef(false)
  const signOutTimeout = useRef<NodeJS.Timeout | null>(null)
  
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      // Pomiń processing podczas role switching
      if (isRoleSwitching) {
        console.log('🔄 Role switching in progress, skipping auth processing')
        return
      }
      
      // Pomiń processing jeśli trwa signOut operation
      if (isSigningOut.current) {
        console.log('🔄 SignOut in progress, skipping auth processing')
        return
      }
      
      if (u) {
        // Sprawdź cache żeby uniknąć duplikatów zapytań
        if (lastAuthCheck.current === u.uid && profileCache.current[u.uid]) {
          console.log('📋 Using cached profile for', u.uid)
          return
        }
        
        lastAuthCheck.current = u.uid
        
        try {
          // Check if user profile exists - tylko jedno zapytanie
          const existingProfile = await ProfilesService.getProfile(u.uid)
          profileCache.current[u.uid] = existingProfile
          
          if (existingProfile) {
            // Profile exists - check if it's deleted
            if (existingProfile.deleted) {
              console.warn('User marked as deleted, signing out:', existingProfile.email)
              alert('To konto zostało usunięte przez administratora. Zostaniesz wylogowany.')
              
              // Używamy flagi żeby uniknąć pętli
              isSigningOut.current = true
              await signOut(auth)
              
              // Reset flagi po 2 sekundach (safety timeout)
              signOutTimeout.current = setTimeout(() => {
                isSigningOut.current = false
              }, 2000)
              return
            }
            
            // Handle based on role
            if (existingProfile.role === 'client') {
              // Client logic
              if (!clientRestoredFromSession) {
                const user = { id: existingProfile.uid, username: existingProfile.username, email: existingProfile.email }
                setIsClientLoggedIn(true)
                setCurrentClient(user)
                recordLoginDebounced(user.id)
              } else {
                console.log('🚫 Client already restored from session, skipping auto-login')
              }
            } else if (existingProfile.role === 'admin') {
              // Admin logic - verify permissions only if admin is logged in locally
              if (isAdminLoggedIn) {
                console.log('✅ Admin permissions verified, token refreshed')
              } else {
                console.log('Admin authenticated, but not auto-logging to client panel')
              }
            }
          } else {
            // No profile exists - handle first time login
            console.log('No profile found for authenticated user:', u.uid)
            console.warn('Profile not found, signing out:', u.uid)
            
            // Używamy flagi żeby uniknąć pętli
            isSigningOut.current = true
            await signOut(auth)
            
            // Reset flagi po 2 sekundach (safety timeout)
            signOutTimeout.current = setTimeout(() => {
              isSigningOut.current = false
            }, 2000)
          }
        } catch (error) {
          console.error('Error in auth state change handler:', error)
          // W przypadku błędu też resetuj flagę
          isSigningOut.current = false
        }
      } else {
        // User signed out - clear both admin and client states
        isSigningOut.current = false // Reset flagi przy wylogowaniu
        if (signOutTimeout.current) {
          clearTimeout(signOutTimeout.current)
          signOutTimeout.current = null
        }
        
        setIsClientLoggedIn(false)
        setCurrentClient(null)
        clearUserSession()
        setClientRestoredFromSession(false)
        
        if (isAdminLoggedIn) {
          console.warn('⚠️ Admin logged out from Firebase Auth')
          handleAdminLogout()
        }
      }
    })
    
    // Cleanup function
    return () => {
      unsub()
      if (signOutTimeout.current) {
        clearTimeout(signOutTimeout.current)
      }
    }
  }, [isRoleSwitching]) // Dodajemy isRoleSwitching do dependencies żeby listener reagował na zmiany

  // Admin permissions sprawdzane w głównym listener - usunięte duplikacje

  // Role conflicts i admin permissions obsługiwane w głównym listener - usunięte duplikacje

  // removed Firebase inline test UI and helpers

  // Preload common images on app startup
  useEffect(() => {
    preloadCommonImages()
  }, [])

  // Grid images are now preloaded in NewModelSelector component

  // Auto-logout on page refresh/close if "Remember Me" not checked
  // Używamy useRef żeby uniknąć ponownego dodawania listenerów
  const loginStates = useRef({ isClientLoggedIn: false, isAdminLoggedIn: false })
  
  useEffect(() => {
    // Aktualizuj ref gdy stany się zmieniają
    loginStates.current = { isClientLoggedIn, isAdminLoggedIn }
  }, [isClientLoggedIn, isAdminLoggedIn])
  
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('🔄 Page unload detected, checking remember me preferences...')
      
      // Check client session - używamy ref zamiast stanu z dependency
      const clientSession = getUserSession()
      if (loginStates.current.isClientLoggedIn && (!clientSession || !clientSession.rememberMe)) {
        console.log('🚪 Auto-logout client (remember me not checked)')
        clearUserSession()
      }
      
      // Check admin session - używamy ref zamiast stanu z dependency  
      const adminSession = getAdminSession()
      if (loginStates.current.isAdminLoggedIn && !adminSession.rememberMe) {
        console.log('🚪 Auto-logout admin (remember me not checked)')
        clearAdminSession()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, []) // Pusty dependency array - listener dodawany tylko raz

  // Reset configuration when variant changes (standard <-> lift)
  useEffect(() => {
    if (currentModel && config.variant && previousVariant && config.variant !== previousVariant) {
      // Reset all options except variant when variant changes
      resetConfigExceptVariant(config.variant)
    }
    setPreviousVariant(config.variant)
  }, [config.variant, currentModel, previousVariant, resetConfigExceptVariant, setPreviousVariant])

  const handleModelChange = (model: ModelData | null, groupKey?: string, modelKey?: string) => {
    // Update state
    if (groupKey !== undefined) setSelectedGroup(groupKey)
    if (modelKey !== undefined) setSelectedModel(modelKey)
    
    // Preload all images for the category when group changes
    if (groupKey && groupKey !== selectedGroup) {
      const categoryModels = modelsData[groupKey]
      if (categoryModels) {
        console.log('[App] Category changed to:', groupKey)
        preloadCategoryImages(categoryModels)
      }
    }
    
    setCurrentModel(model, groupKey)
    // Reset configuration when model changes
    resetConfig()
    // Reset previous variant tracking
    setPreviousVariant('')
    
    // Auto-select variant if only one is available
    if (model) {
      const liftAvailable = isLiftAvailable(model)
      const standardAvailable = isStandardAvailable(model)
      
      // If only one variant is available, auto-select it
      if (liftAvailable && !standardAvailable) {
        updateConfig('variant', 'lift')
      } else if (standardAvailable && !liftAvailable) {
        updateConfig('variant', 'standard')
      }
      
      // Auto-select glass if only one option is available
      if (model.glassType === 'narozna') {
        const glassTypes = new Set<string>();
        
        if (model.basePrices?.standard) {
          Object.keys(model.basePrices.standard).forEach(glassType => {
            glassTypes.add(glassType);
          });
        }
        
        if (model.basePrices?.lift) {
          Object.keys(model.basePrices.lift).forEach(glassType => {
            glassTypes.add(glassType);
          });
        }
        
        if (glassTypes.size === 1) {
          const onlyGlassType = Array.from(glassTypes)[0];
          updateConfig('glass', onlyGlassType);
        }
      }
    }
  }

  // Wrapper function for search results that finds and passes the model
  const handleProductSelect = (groupKey: string, modelKey: string) => {
    const modelData = findModelData(modelsData, groupKey, modelKey)
    if (modelData) {
      handleModelChange(modelData, groupKey, modelKey)
    } else {
      console.warn('Model not found:', groupKey, modelKey)
    }
  }

  const handleShowSummary = () => {
    if (!validation.isValid) {
      setShowValidationMessage(true)
      return
    }
    console.log('FloatingPrice clicked, currentModel:', currentModel)
    setShowSummary(true)
  }

  const handleHeaderClientClick = () => {
    if (isClientLoggedIn) {
      setShowClientPanel(true)
    } else if (isAdminLoggedIn) {
      setShowAdminPanel(true)
    } else {
      // Temporarily show a 'coming soon' modal instead of opening login
      setShowComingSoon(true)
    }
  }

  // If user navigates to /login directly, open the client login modal
  useEffect(() => {
    try {
      const path = window.location.pathname || ''
      if (path.replace(/\/+$/, '') === '/login') {
        setShowClientLogin(true)
        // ensure coming soon is closed
        setShowComingSoon(false)
      }
    } catch (err) {
      // ignore
    }
  }, [])

  const handleAdminPanelClose = () => {
    setShowAdminPanel(false)
  }

  const handleAdminLogout = async () => {
    try { await signOut(auth) } catch {}
    setIsAdminLoggedIn(false)
    clearAdminSession()
    setShowAdminPanel(false)
  }

  const handleAdminLogin = (rememberMe: boolean = false) => {
    setIsAdminLoggedIn(true)
    setAdminSession(rememberMe)
    setShowClientLogin(false)
    setShowAdminPanel(true)
  }

  const handleSiteLogout = () => {
    localStorage.removeItem('configurator_auth')
    sessionStorage.removeItem('configurator_auth')
    setIsAuthenticated(false)
  }

  const handleClientLogin = (user: { id: string; username: string; email: string }, rememberMe: boolean = false) => {
    setIsClientLoggedIn(true)
    setCurrentClient(user)
    setUserSession(user, rememberMe)
    setShowClientLogin(false)
    setShowClientPanel(true)
    setClientRestoredFromSession(false) // This is a fresh login, not a restoration
    
    // Record login in history (duplicate prevention is handled in LoginHistoryService)
    recordLoginDebounced(user.id)
  }

  const handleClientPanelClose = () => {
    setShowClientPanel(false)
  }

  const handleClientLogout = async () => {
    try { await signOut(auth) } catch {}
    setIsClientLoggedIn(false)
    setCurrentClient(null)
    clearUserSession()
    setShowClientPanel(false)
    setShowClientLogin(false)
    setClientRestoredFromSession(false)
  }

  // removed handleAddModel (no longer used)

  const handleProductCodeSubmit = (productCode: string) => {
    console.log('Processing product code:', productCode)
    
    // Parse the product code
    const parsed = loadConfigFromCode(productCode)
    console.log('Parsed result:', parsed)
    
    if (parsed.modelGroup && parsed.modelKey) {
      console.log('Looking for model:', parsed.modelKey, 'in group:', parsed.modelGroup)
      console.log('Available groups:', Object.keys(modelsData))
      console.log('Available models in group:', Object.keys(modelsData[parsed.modelGroup] || {}))
      
      // Set the group and model in selector
      setSelectedGroup(parsed.modelGroup)
      setSelectedModel(parsed.modelKey)
      
      // Find and set the model
      const modelData = findModelData(modelsData, parsed.modelGroup, parsed.modelKey)
      if (modelData) {
        setCurrentModel(modelData)
        console.log('Model set from product code:', modelData.name)
        console.log('Group set to:', parsed.modelGroup)
        console.log('Model key set to:', parsed.modelKey)
      } else {
        console.warn('Model not found for code:', parsed.modelGroup, parsed.modelKey)
        console.warn('Available models in this group:', Object.keys(modelsData[parsed.modelGroup] || {}))
      }
    } else {
      console.warn('Failed to parse model group or key:', { modelGroup: parsed.modelGroup, modelKey: parsed.modelKey })
    }
    
    // Configuration is already applied by loadConfigFromCode
    console.log('Configuration applied from product code:', parsed.config)
  }

  // Removed Drive upload and full save handlers

  // Simple inline ProductCodeModal component
  const ProductCodeModal = ({ isOpen, onClose, onSubmit }: {
    isOpen: boolean
    onClose: () => void
    onSubmit: (code: string) => void
  }) => {
    const [code, setCode] = useState('')
    
    if (!isOpen) return null
    
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Wprowadź Kod Produktu</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="np. DP-2S-ST-S2-HL-C1-H1-B0"
              className="form-input"
            />
            <button 
              className="btn-primary btn-no-radius"
              onClick={() => {
                if (code.trim()) {
                  onSubmit(code.trim().toUpperCase())
                  setCode('')
                  onClose()
                }
              }}
              style={{ marginTop: '30px' }}
            >
              Zastosuj
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      {!isAuthenticated && !showPasswordChangedModal ? (
        <LoginModal onLoginSuccess={() => setIsAuthenticated(true)} />
      ) : (
        <div className={`app-fade-stage ${isAppEntranceVisible ? 'is-visible' : ''}`}>
          <ThemeHeader onSiteLogout={handleSiteLogout} />
          <Header 
            onClientClick={handleHeaderClientClick} 
            isAdminLoggedIn={isAdminLoggedIn}
            isClientLoggedIn={isClientLoggedIn}
            onShowProductCodeModal={() => setShowProductCodeModal(true)}
        modelsData={modelsData}
        onProductSelect={handleProductSelect}
      />

      {/* Firebase test panel removed from UI per requirement */}
      <main className="configurator">
        <section className="options">
          <NewModelSelector 
            modelsData={modelsData}
            modelGroups={modelGroups}
            onModelChange={handleModelChange}
            selectedGroup={selectedGroup}
            selectedModel={selectedModel}
          />
          
          {currentModel && (
            <ConfiguratorOptions
              model={currentModel}
              config={config}
              onConfigChange={updateConfig}
              onSpecialConfigChange={checkAndHandleLiftSelection}
              priceMode={priceMode}
            />
          )}
        </section>
        
        <PreviewPanel
          model={currentModel}
          config={config}
          pricing={pricing}
        />
      </main>
      
      <FloatingPrice 
        pricing={pricing} 
        onShowSummary={handleShowSummary}
        isConfigurationValid={validation.isValid}
        canShowPrice={validation.canShowPrice}
        hasModel={!!currentModel}
        priceMode={priceMode}
        setPriceMode={setPriceMode}
      />

      {/* Removed full configuration save button */}
      
      {showSummary && currentModel && validation.isValid && (
        <Suspense fallback={<div>Generowanie podsumowania...</div>}>
          <SummaryModal 
            model={currentModel} 
            config={config} 
            pricing={pricing}
            onClose={() => setShowSummary(false)}
            priceMode={priceMode}
            isAdminLoggedIn={isAdminLoggedIn}
            isClientLoggedIn={isClientLoggedIn}
          />
        </Suspense>
      )}

      {showValidationMessage && (
        <ValidationMessage
          missingFieldLabels={validation.missingFieldLabels}
          onClose={() => setShowValidationMessage(false)}
        />
      )}

      {/* Client auth components */}
      <ClientLogin
        isOpen={showClientLogin}
        onClose={() => setShowClientLogin(false)}
        onLogin={handleClientLogin}
        onAdminLogin={handleAdminLogin}
        onSetRoleSwitching={setIsRoleSwitching}
      />
      <Suspense fallback={<div style={{ display: 'none' }}>Ładowanie...</div>}>
        <ClientPanel
          isOpen={showClientPanel}
          onClose={handleClientPanelClose}
          isLoggedIn={isClientLoggedIn}
          currentUser={currentClient ?? undefined}
          onLogout={handleClientLogout}
          onLoadConfiguration={handleProductCodeSubmit}
          onProfileUpdated={(u) => {
            setCurrentClient(u)
            // Update session with same remember preference
            const currentSession = getUserSession()
            if (currentSession) {
              setUserSession(u, currentSession.rememberMe)
            }
          }}
        />
      </Suspense>

      <Suspense fallback={<div style={{ display: 'none' }}>Ładowanie...</div>}>
        <AdminPanel
          isOpen={showAdminPanel}
          onClose={handleAdminPanelClose}
          isLoggedIn={isAdminLoggedIn}
          onLogout={handleAdminLogout}
        />
      </Suspense>

      {/* Product Code Modal */}
      <ProductCodeModal
        isOpen={showProductCodeModal}
        onClose={() => setShowProductCodeModal(false)}
        onSubmit={handleProductCodeSubmit}
      />

      {/* LIFT Notification Modal */}
      <LiftNotificationModal
        isOpen={showLiftModal}
        onClose={closeLiftModal}
      />
      <ComingSoonModal
        isOpen={showComingSoon}
        onClose={() => setShowComingSoon(false)}
        onOpenClientLogin={() => {
          setShowClientLogin(true)
          setShowComingSoon(false)
        }}
      />
      <UpdateModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
      />
      {showInstructionModal && (
        <InstructionModal onClose={() => setShowInstructionModal(false)} />
      )}
      
      {/* Password Changed Modal */}
      {showPasswordChangedModal && (
        <PasswordChangedModal
          onConfirm={() => {
            setShowPasswordChangedModal(false)
            setIsAuthenticated(false) // Now logout
            window.location.reload()
          }}
        />
      )}
      
      <ThemeFooter />
        </div>
      )}
    </div>
  )
}

export default App
